/* eslint-disable @vp/swan/no-deprecated */
/* eslint-disable @vp/swan/required-accessibility-props */
import React, { useState } from 'react';

import Slider from 'react-slick';

import { Box, Carousel, CarouselSlide, Visible } from '@vp/swan';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { Image } from '../domain/Image';
import { Slide } from '../domain/Slide';
import { SlideArray } from '../domain/SlideArray';
import { Video } from '../domain/Video';
import { GalleryId } from '../libs/constants';
import { pauseVideo } from '../libs/videoActions';

import { GalleryImage } from './GalleryImage';
import { GalleryVideo } from './GalleryVideo';
import { ZoomDialog } from './zoom/ZoomDialog';
import { ZoomedDisplayData } from './zoom/ZoomedDisplayData';

interface GalleryProps {
  nav: Slider | undefined;
  setNav: (value: Slider | undefined) => void;
  mediaArray: SlideArray;
  currentSlide?: number;
  zoomContainerId?: string;
  tenant: string;
  canonicalDomain: string;
  infinite: boolean;
}

export const Gallery: React.FC<GalleryProps> = (props) => {
  const { t } = useLocalizations();
  const [zoomData, setZoomData] = useState<ZoomedDisplayData>(new ZoomedDisplayData());

  const displayZoomDialog = (slide: Slide): void => {
    if (props.zoomContainerId && slide.isImage) {
      setZoomData(new ZoomedDisplayData((slide.media as Image).zoom, slide.getAlt(t), true));
    }
  };

  const handleSlideChange = (oldIndex: number): void => {
    if (props.mediaArray.all[oldIndex].isVideo) {
      pauseVideo(props.mediaArray.all[oldIndex]?.galleryId);
    }
  };

  const renderCarouselSlides = (withZoom: boolean = false): React.ReactNode[] => {
    return props.mediaArray.all.map((slide: Slide) => {
      return (
        <CarouselSlide
          key={slide.galleryId}
          onClick={withZoom ? () => displayZoomDialog(slide) : undefined}
          aria-label={slide.getImageAriaLabel(t)}
        >
          {slide.isVideo ? (
            <GalleryVideo
              video={slide.media as Video}
              playerId={slide.galleryId}
              tenant={props.tenant}
              canonicalDomain={props.canonicalDomain}
            />
          ) : (
            <GalleryImage
              image={slide.media as Image}
              alt={slide.getAlt(t)}
              zoomContainerId={props.zoomContainerId}
            />
          )}
        </CarouselSlide>
      );
    });
  };

  const baseCarouselProps: React.ComponentProps<typeof Carousel> = {
    className: 'carousel-dots',
    asNavFor: props.nav,
    arrows: false,
    fade: false,
    ref: (slider: Slider | null) => props.setNav(slider ?? undefined),
    accessibleTextForNext: t('next'),
    accessibleTextForPrevious: t('previous'),
    accessibleTextForDots: [],
    currentSlide: props.currentSlide,
    infinite: props.infinite,
    'aria-label': t('image-container-label'),
    beforeChange: handleSlideChange,
  };

  return (
    <Box id={GalleryId}>
      <Visible xs sm>
        <Carousel {...baseCarouselProps} progressIndicator="dots" dots={true}>
          {renderCarouselSlides(true)}
        </Carousel>
        {props.zoomContainerId && (
          <ZoomDialog zoomData={zoomData} onClose={() => setZoomData(new ZoomedDisplayData())} />
        )}
      </Visible>

      <Visible md lg xl>
        <Carousel {...baseCarouselProps} progressIndicator="none" dots={false}>
          {renderCarouselSlides()}
        </Carousel>
      </Visible>
    </Box>
  );
};
